import { useMemo } from 'react';

import { useOperationGetById } from 'api/requests';
import { useTranslation } from 'libs/i18n';
import { RoutesId, useNavigate } from 'libs/navigation';
import { OperationFormType } from 'modules/operation/common/forms/operationForm';

import { getOperationInitialValue } from '../helpers';

import { useChangeStatusAction } from './useChangeStatusAction';
import { useDeleteAction } from './useDeleteAction';
import { useUpdateOperationAction } from './useUpdateOperationAction';

export const useOperation = () => {
  const { params } = useNavigate<{
    operationId: string;
  }>();
  const { t } = useTranslation();

  const { operation, loading, error } = useOperationGetById(
    params.operationId!
  );

  const { submitLoading, onDisableEdit, onEnableEdit, onSubmit } =
    useUpdateOperationAction(operation);

  const {
    isOpenDialogChangeStatus,
    onCloseDialogChangeStatus,
    onOpenDialogChangeStatus,
    onSuccessChangeStatus,
  } = useChangeStatusAction(operation);

  const {
    loadingDelete,
    isOpenDialogConfirmDelete,
    onDelete,
    onCloseDialogConfirmDelete,
    onOpenDialogConfirmDelete,
  } = useDeleteAction(operation);

  const title =
    t('operation.operation.title') +
    (operation?.number ? ` №${operation.number}` : '');

  const breadcrumbsTitles = {
    [RoutesId.operation]: title,
  };

  // GMT+5 (Алматы)
  // TODO: Feature request to get broker timezone offset from API
  const brokerTimezoneOffset = 18000;

  const initialValues: Partial<OperationFormType> | undefined = useMemo(
    () =>
      operation
        ? getOperationInitialValue(t, operation, brokerTimezoneOffset)
        : undefined,
    [t, operation, brokerTimezoneOffset]
  );

  return {
    loading: loading && !operation,
    notFound: !!error || (!operation && !loading),
    operation,
    title,
    breadcrumbsTitles,
    brokerTimezoneOffset,
    initialValues,
    submitLoading,
    isOpenDialogChangeStatus,
    isOpenDialogConfirmDelete,
    loadingDelete,
    onCloseDialogChangeStatus,
    onSuccessChangeStatus,
    onOpenDialogChangeStatus,
    onCloseDialogConfirmDelete,
    onOpenDialogConfirmDelete,
    onDelete,
    onSubmit,
    onEnableEdit,
    onDisableEdit,
  };
};
